import React, { useState } from 'react';
import { Button, Input, Modal, PageHeader, Tag, message } from 'udesk-ui';
import styled from 'styled-components';
import Udesk from 'Udesk';
import { useRequest } from 'ahooks';
import { getAsrHotWordsModelsById } from 'src/api/asrHotWordsModels/{id}';
import { getCorporaHotWordsListByType } from 'src/api/corpora/hotWordsList/{type}';
import { postCorporaStaticTotalHotWords } from 'src/api/corpora/staticTotalHotWords';
import { postCorporaHotWords } from 'src/api/corpora/hotWords';
import { deleteCorporaById } from 'src/api/corpora/{id}';
import { AsrHotWordsModelFoundResponse, CorporaCommonFoundResponse } from 'src/api/types';
import isNil from 'lodash/isNil';
import { getCurrentRouteName } from 'Udesk/system/subApp';
import UdeskLocales from 'UdeskLocales';
import './index.scss';

const Wrapper = styled.div``;

const Tip = styled.div`
    color: rgba(0, 0, 0, 0.25);
    padding: 0 0 20px 0;
`;

const Content = styled.div``;

const Extra = styled.div`
    padding: 20px 0 0 0;
`;

const Template = React.memo((props: any) => {
    const { match } = props;

    const id = match.params?.id;

    const onBack = () => {
        Udesk.ui.routing.transitionTo({
            history: props.history,
            routeName: getCurrentRouteName('smartWordsLibraryHotWords'),
        });
    };

    const [model, setModel] = useState<AsrHotWordsModelFoundResponse>();

    useRequest(() => getAsrHotWordsModelsById({ segments: { id } }), {
        onSuccess: (res) => {
            setModel(res.data);
        },
    });

    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const limitQuantity = 300;

    const { refresh: refreshTotalQuantity } = useRequest(
        () => postCorporaStaticTotalHotWords({ params: { asrEngine: model?.asrEngine! } }),
        {
            ready: !!model?.asrEngine,
            onSuccess: (res) => {
                setTotalQuantity(res.data ?? 0);
            },
        }
    );

    const [tags, setTags] = useState<CorporaCommonFoundResponse[]>();

    const { refresh: refreshTags } = useRequest(
        () =>
            getCorporaHotWordsListByType({
                segments: { type: 8 },
                params: { asrEngine: model?.asrEngine! },
            }),
        {
            ready: !!model?.asrEngine,
            onSuccess: (res) => {
                setTags(res.data ?? []);
            },
        }
    );

    const [hotWords, setHotWords] = useState<string>();

    const onValueChange = (v: string) => {
        setHotWords(v);
    };

    const onSubmit = () => {
        postCorporaHotWords({ corporaName: hotWords, asrEngine: model?.asrEngine }).then(() => {
            refreshTotalQuantity();
            refreshTags();
            setHotWords(undefined);
        });
    };

    const onDelete = (id?: number) => {
        if (!isNil(id)) {
            Modal.confirm({
                title: /* 确定删除吗？ */ UdeskLocales['current'].pages.semanticIntelligence
                    .smartWordsLibrary.hotWords.index.areYouSureToDeleteIt,
                onOk: () => {
                    deleteCorporaById({ segments: { id: id! } })
                        .then(() => {
                            message.success({
                                content:
                                    /* 删除成功 */ UdeskLocales['current'].pages
                                        .semanticIntelligence.smartWordsLibrary.hotWords.index
                                        .deleteSuccessful,
                            });
                        })
                        .finally(() => {
                            refreshTotalQuantity();
                            refreshTags();
                        });
                },
            });
        }
    };

    return (
        <Wrapper>
            <PageHeader
                className="hot-words-detail-header"
                onBack={onBack}
                title={model?.modelName}
            />

            <Tip>
                {/* 当前热词 */}
                {
                    UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.hotWords
                        .index.currentHotWords
                }
                {totalQuantity}/{limitQuantity}
                {/* 个 */}
                {
                    UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary.hotWords
                        .index.one
                }
            </Tip>

            <Content>
                <Input.Group compact>
                    <Input
                        style={{ width: 500 }}
                        placeholder={
                            /* 请输入热词，多个热词以中文或英文逗号间隔，最大长度12个字符 */ UdeskLocales[
                                'current'
                            ].pages.semanticIntelligence.smartWordsLibrary.hotWords.index
                                .pleaseEnterAHotWordMultipleHotWordsAreSeparatedByCommasInChineseOrEnglishTheMaximumLengthIsCharacters
                        }
                        value={hotWords}
                        onChange={(e) => onValueChange(e.target.value)}
                    />
                    <Button type="primary" onClick={() => onSubmit()} disabled={!hotWords}>
                        {/* 提交 */}
                        {
                            UdeskLocales['current'].pages.semanticIntelligence.smartWordsLibrary
                                .hotWords.index.submission
                        }
                    </Button>
                </Input.Group>

                <Extra>
                    {tags?.map((tag) => {
                        return (
                            <Tag
                                key={tag.id}
                                title={tag.corporaName}
                                closable
                                onClose={(e) => {
                                    e.preventDefault();
                                    onDelete(tag.id);
                                }}
                                style={{ marginBottom: 8 }}
                            >
                                {tag.corporaName}
                            </Tag>
                        );
                    })}
                </Extra>
            </Content>
        </Wrapper>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
